import { noop } from 'lodash/fp';
import styled from 'styled-components';
import { OrderItem, WithScope, WithServiceId, WithTimeUnit } from '@mews-commander/types';
import { Checkbox } from '@optimus-web/core';
import { getToken } from '@optimus-web/common';
import { TooltippedSpan, useOnScreen } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { BillListItem } from '../../../../components';
import { isOrderItemClosed, isOrderItemTaxCorrection } from '../../../../rebate/selectors/orderItems';
import { OrderItemListItemText } from '../OrderItemListItemText';
import { SCROLL_TO_ORDER_ITEM_ANCHOR, isOrderItemHighlighted } from '../../../../ducks/billing';
import { ORDER_ITEM_CHECKBOX_DATA_TEST_ID } from '../../constants';

interface OrderItemListItemProps extends WithServiceId, WithTimeUnit, WithScope {
    orderItem: OrderItem;
    selected: boolean;
    showNotes?: boolean;
    toggleSelect(): void;
    isRebated: boolean;
    depth?: number;
    scope: string;
}

const Highlighted = styled.div`
    animation: ${(props: { pulse: boolean }) => (props.pulse ? 'pulse 1s ease-in-out' : 'none')};
    animation-delay: 0.5s;

    @keyframes pulse {
        50% {
            background-color: ${getToken('legacyTagBackgroundDenim')};
        }
    }
`;

export const OrderItemListItem: React.FC<OrderItemListItemProps> = ({
    orderItem,
    selected,
    timeUnit,
    toggleSelect,
    showNotes,
    isRebated,
    depth,
    scope,
}) => {
    const isClosed = isOrderItemClosed(orderItem);
    const isTaxCorrection = isOrderItemTaxCorrection(orderItem);
    const isDisabled = (isRebated && isClosed) || isTaxCorrection;
    const isThisItemHighlighted = useCommanderSelector((state) =>
        isOrderItemHighlighted(state, { orderItemId: orderItem.id, scope })
    );
    const [listItemRef, isInView] = useOnScreen();

    return (
        <Highlighted
            pulse={isThisItemHighlighted && isInView}
            {...{ [SCROLL_TO_ORDER_ITEM_ANCHOR]: orderItem.id }}
            ref={listItemRef}
        >
            <BillListItem
                data-order-item-row={orderItem.id}
                depth={depth}
                key={orderItem.id}
                primaryText={
                    <OrderItemListItemText
                        orderItem={orderItem}
                        isRebated={isRebated}
                        showNotes={showNotes}
                        timeUnit={timeUnit}
                    />
                }
                leftItem={
                    <TooltippedSpan tooltip={isTaxCorrection ? lt('RebateItemsCheckboxDisabledTooltip') : null}>
                        <Checkbox
                            disabled={isDisabled}
                            checked={selected}
                            data-test-id={ORDER_ITEM_CHECKBOX_DATA_TEST_ID}
                        />
                    </TooltippedSpan>
                }
                onClick={isDisabled ? noop : toggleSelect}
            />
        </Highlighted>
    );
};
